import {
  ApiClientProvider,
  ConfigurationParameters,
  Configuration,
  ErrorContext,
  ResponseError
} from 'bff';
import { PropsWithChildren } from 'react';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { logger } from '@sanlam-glacier/app-common/modules/logger';
import { useAuth, getAuthHeader } from 'auth';
import { secondsToMilliseconds } from 'date-fns';
import { displayToast } from '@sanlam-glacier/app-common/modules/toasts';

const notFoundStatusCode = 404;

interface QueryProviderProps {
  bffApiUrl: string;
  localJwtProvider?: string;
  actingAsIntermediaryId?: string;
  configureAccessToken?: boolean;
}

export const QueryProvider = ({
  children,
  bffApiUrl,
  localJwtProvider,
  configureAccessToken
}: PropsWithChildren<QueryProviderProps>) => {
  const { logout } = useAuth();

  const onUnauthorised = async (response?: Response): Promise<void> => {
    if (response?.status === 401) {
      await logout();
    }
  };
  const configParams: ConfigurationParameters = {
    basePath: bffApiUrl,
    middleware: [
      {
        onError: async (context: ErrorContext) => {
          await onUnauthorised(context.response);
        },
        post: async (context) => {
          await onUnauthorised(context.response);
        }
      }
    ],
    apiKey: () => {
      return getAuthHeader(bffApiUrl, configureAccessToken, localJwtProvider);
    }
  };

  const queryClient = new QueryClient({
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        logger.error({ message: 'Query error', object: { error } });
        const responseError = error as ResponseError;
        const statusCode = responseError.response?.status;
        if (statusCode === notFoundStatusCode) return;
        const hideErrorToast = !!mutation.meta?.hideErrorToast;
        const errorMessage =
          mutation.meta?.errorMessage ??
          'Some information isn’t available right now. Please try again later.';
        const correlationId = responseError.response?.headers?.get('X-Correlationid');
        const toastKey = mutation.options.mutationKey?.join('-') ?? `error-${correlationId}`;
        const fullErrorMessage =
          correlationId !== null
            ? `${errorMessage} \nSupport ID: ${correlationId}`
            : `${errorMessage}`;
        if (!hideErrorToast)
          displayToast({
            toastId: toastKey,
            title: 'Something went wrong',
            message: fullErrorMessage,
            status: 'error',
            options: {
              disableTimeout: true
            }
          });
        void responseError.response.json().then((res) => {
          logger.error({
            message: `${errorMessage}`,
            object: {
              name: ResponseError.name,
              message: responseError.message,
              response: res,
              status: statusCode
            },
            correlationId: correlationId ?? undefined
          });
        });
      }
    }),
    queryCache: new QueryCache({
      onError: (error, query) => {
        const responseError = error as ResponseError;
        const statusCode = responseError.response?.status;
        if (statusCode === notFoundStatusCode) return;
        const hideErrorToast = !!query.meta?.hideErrorToast;
        const errorMessage =
          query.meta?.errorMessage ??
          'Some information isn’t available right now. Please try again later.';
        const toastKey = query.queryKey.join('-');
        const correlationId = responseError.response?.headers?.get('X-Correlationid');
        const fullErrorMessage =
          correlationId !== null
            ? `${errorMessage} \nSupport ID: ${correlationId}`
            : `${errorMessage}`;
        if (!hideErrorToast)
          displayToast({
            toastId: toastKey,
            title: 'Something went wrong',
            message: fullErrorMessage,
            status: 'error',
            options: {
              disableTimeout: true
            }
          });
        void responseError.response.json().then((res) => {
          logger.error({
            message: `${errorMessage}`,
            object: {
              name: ResponseError.name,
              message: responseError.message,
              response: res,
              status: statusCode
            },
            correlationId: correlationId ?? undefined
          });
        });
      }
    }),
    defaultOptions: {
      queries: {
        staleTime: secondsToMilliseconds(30)
      }
    }
  });

  const apiConfig = new Configuration(configParams);

  return (
    <QueryClientProvider client={queryClient}>
      <ApiClientProvider apiConfig={apiConfig}>{children}</ApiClientProvider>
    </QueryClientProvider>
  );
};
